import { DrawerCloseButton, UnsavedChangesModal } from '@components';
import { useView } from '@hooks';
import { L } from '@lib';
import { Button, Drawer } from 'antd';
import React, { useState } from 'react';

interface IProps {
  title: React.ReactNode;
  open: boolean;
  /** The `<form id>` the footer's submit button drives - pass the same id to the SharedForm inside. */
  formId: string;
  submitTitle: string;
  loading?: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

/**
 * A drawer whose whole body is one form. The Cancel and submit buttons sit in the drawer's own
 * footer, so they stay in view however long the form is. Every way out - mask click, Esc, ✕,
 * Cancel - goes through the same "Unsaved changes" prompt the booking drawer uses, whatever the
 * form holds: the drawer is narrow and a stray click outside is easy, so it always asks.
 */
export const FormDrawer: React.FC<IProps> = ({
  title,
  open,
  formId,
  submitTitle,
  loading = false,
  onClose,
  children,
}) => {
  const { isMobile } = useView();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleCloseAttempt = () => setConfirmOpen(true);

  const handleDiscard = () => {
    setConfirmOpen(false);
    onClose();
  };

  return (
    <Drawer
      title={title}
      open={open}
      onClose={handleCloseAttempt}
      styles={{ wrapper: { width: isMobile ? '100%' : 720 } }}
      closable={false}
      extra={<DrawerCloseButton onClick={handleCloseAttempt} />}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleCloseAttempt} disabled={loading}>
            {L('Cancel')}
          </Button>
          <Button type="primary" htmlType="submit" form={formId} loading={loading}>
            {submitTitle}
          </Button>
        </div>
      }
    >
      {children}
      <UnsavedChangesModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onLeave={handleDiscard}
      />
    </Drawer>
  );
};
